function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { getIsPermittedWithAnyOfThesePermissions, getIsPermittedWithNoneOfThesePermissions } from '../common/classes/permissions';
import checkCabinetPermissions from './headerUtils';
import processLegacyCabinets from './legacyCabinets';

/**
 * Returns the list of main nav items for the given user, cabinets, and dashboards.
 * Used by FixedHeader (top layout) and GlobalNavSidebar (sidebar layout) to ensure
 * both use the same nav items and permission logic.
 *
 * Each item: { key, className, href, icon, label }
 *
 * Mirrors the permission checks in fixedHeader.jsx. Cabinet items with a tabGroup.identifier
 * are excluded (they appear in grouped dropdowns in the hierarchical header only).
 */
const getNavItems = ({
  user,
  cabinets: _cabinets = [],
  dashboards: _dashboards = []
}) => {
  if (!user) return [];
  const items = [];

  // File Cabinet — unless deny_report_view_list is set
  if (getIsPermittedWithNoneOfThesePermissions(['deny_report_view_list', 'deny_report_*'], user)) {
    items.push({
      key: 'reportsTab',
      className: 't-h-nav-file-cabinet',
      href: '/#/file-cabinet',
      icon: 'folder-open',
      label: 'File Cabinet'
    });
  }

  // Issues — if issue_show is set and not deny_issue_view_list
  if (getIsPermittedWithNoneOfThesePermissions(['deny_issue_view_list', 'deny_issue_*'], user) && getIsPermittedWithAnyOfThesePermissions(['issue_show'], user)) {
    items.push({
      key: 'issuesTab',
      className: 't-h-nav-issues',
      href: '/#/issues',
      icon: 'th',
      label: 'Issues'
    });
  }

  // Cabinets without a tabGroup.identifier (matches FixedHeader behavior)
  if ((_cabinets === null || _cabinets === void 0 ? void 0 : _cabinets.length) > 0) {
    _cabinets.filter(cabinet => {
      var _cabinet$client;
      return cabinet === null || cabinet === void 0 || (_cabinet$client = cabinet.client) === null || _cabinet$client === void 0 ? void 0 : _cabinet$client.web;
    }).forEach(cabinet => {
      const {
        identifier,
        title,
        client: {
          web: {
            layout: {
              superset
            } = {},
            icon = 'folder-open',
            tabGroup = {}
          } = {}
        } = {}
      } = cabinet;
      const isValidDashboards = _dashboards.some(d => d.name === superset);
      const isInvalidSuperset = superset && !isValidDashboards;
      const hasCabinetPermission = checkCabinetPermissions(cabinet, user);
      if (!tabGroup.identifier && !isInvalidSuperset && hasCabinetPermission) {
        items.push({
          key: `${identifier}Tab`,
          className: `t-h-nav-${identifier}`,
          href: `/#/cabinets/${identifier}`,
          icon,
          label: title
        });
      }
    });
  }

  // Assets — if asset_admin is set and not read_only
  if (getIsPermittedWithNoneOfThesePermissions(['read_only'], user) && getIsPermittedWithAnyOfThesePermissions(['asset_admin'], user)) {
    items.push({
      key: 'assetsTab',
      className: 't-h-nav-assets',
      href: '/#/assets',
      icon: 'briefcase',
      label: 'Asset List'
    });
  }

  // Library — if not read_only and not deny_document_view_list
  if (getIsPermittedWithNoneOfThesePermissions(['read_only'], user) && getIsPermittedWithNoneOfThesePermissions(['deny_document_view_list', 'deny_document_*'], user)) {
    items.push({
      key: 'docsTab',
      className: 't-h-nav-library',
      href: '/#/library',
      icon: 'book',
      label: 'Library'
    });
  }
  return items;
};
const appendCabinetToNavGroups = (dashboards, user, groupedCabinets, ungroupedCabinets) => cabinet => {
  const {
    identifier,
    title,
    path,
    client: {
      web: {
        layout: {
          superset
        } = {},
        icon = 'folder-open',
        tabGroup = {},
        sortOrder = 0
      } = {}
    } = {},
    directFormType
  } = cabinet;
  const isValidDashboards = dashboards.some(dashboard => dashboard.name === superset);
  const isInvalidSuperset = superset && !isValidDashboards;
  const hasCabinetPermission = checkCabinetPermissions(cabinet, user);
  if (!isInvalidSuperset && hasCabinetPermission) {
    const cabinetEntry = {
      identifier,
      title,
      path,
      icon,
      sortOrder,
      directFormType
    };
    if (tabGroup.identifier) {
      if (!groupedCabinets[tabGroup.identifier]) {
        // eslint-disable-next-line no-param-reassign
        groupedCabinets[tabGroup.identifier] = [];
      }
      groupedCabinets[tabGroup.identifier].push(cabinetEntry);
    } else {
      ungroupedCabinets.push(cabinetEntry);
    }
  }
};

/**
 * Match header.jsx: customers with tab groups, legacy cabinets, or direct-form creation
 * use HierarchicalHeader in top layout.
 */
export const hasAdminTabGroupCabinets = (cabinets = []) => cabinets.some(cabinet => {
  var _cabinet$client2;
  return (cabinet === null || cabinet === void 0 || (_cabinet$client2 = cabinet.client) === null || _cabinet$client2 === void 0 || (_cabinet$client2 = _cabinet$client2.web) === null || _cabinet$client2 === void 0 || (_cabinet$client2 = _cabinet$client2.tabGroup) === null || _cabinet$client2 === void 0 ? void 0 : _cabinet$client2.identifier) === 'admin';
});
export const shouldUseHierarchicalNav = (cabinets = []) => {
  if (!(cabinets !== null && cabinets !== void 0 && cabinets.length)) return false;
  const hasGroupedCabinets = cabinets.some(cabinet => {
    var _cabinet$client3;
    return (cabinet === null || cabinet === void 0 || (_cabinet$client3 = cabinet.client) === null || _cabinet$client3 === void 0 || (_cabinet$client3 = _cabinet$client3.web) === null || _cabinet$client3 === void 0 || (_cabinet$client3 = _cabinet$client3.tabGroup) === null || _cabinet$client3 === void 0 ? void 0 : _cabinet$client3.identifier) && cabinet.client.web.tabGroup.identifier !== 'admin';
  });
  const refersLegacyCabinets = cabinets.some(cabinet => cabinet.identifier.includes('legacy'));
  const hasDirectFormCreation = cabinets.some(cabinet => !!(cabinet !== null && cabinet !== void 0 && cabinet.directFormType));
  return hasGroupedCabinets || refersLegacyCabinets || hasDirectFormCreation;
};

/**
 * Hierarchical nav routes admin surfaces through admin tab-group cabinets, so the Admin cog
 * only belongs there when the account defines them. Flat nav has no other admin surface and
 * always keeps it. Used by the sidebar, which serves both layouts.
 */
export const shouldMountAdminMenu = (cabinets = []) => !shouldUseHierarchicalNav(cabinets) || hasAdminTabGroupCabinets(cabinets);
export const partitionCabinetsForNav = ({
  user,
  cabinets: _cabinets2 = [],
  dashboards: _dashboards2 = []
}) => {
  const groupedCabinets = {};
  const ungroupedCabinets = [];
  if (_cabinets2.length > 0) {
    processLegacyCabinets(_cabinets2).forEach(appendCabinetToNavGroups(_dashboards2, user, groupedCabinets, ungroupedCabinets));
  }
  return {
    groupedCabinets,
    ungroupedCabinets
  };
};
const getTabGroupSortOrder = (tabGroupIdentifier, tabGroups = []) => {
  var _groupInfo$sortOrder;
  const groupInfo = tabGroups.find(group => group.identifier === tabGroupIdentifier);
  return (_groupInfo$sortOrder = groupInfo === null || groupInfo === void 0 ? void 0 : groupInfo.sortOrder) !== null && _groupInfo$sortOrder !== void 0 ? _groupInfo$sortOrder : 0;
};
export { getTabGroupSortOrder };

/**
 * Hierarchical nav entries for sidebar (and shared cabinet grouping with top layout).
 * Entry types: link | directForm | dropdown
 */
export const getHierarchicalNavEntries = ({
  user,
  cabinets: _cabinets3 = [],
  dashboards: _dashboards3 = [],
  tabGroups: _tabGroups = []
}) => {
  const entries = [];
  const {
    groupedCabinets,
    ungroupedCabinets
  } = partitionCabinetsForNav({
    user,
    cabinets: _cabinets3,
    dashboards: _dashboards3
  });
  ungroupedCabinets.sort((a, b) => a.sortOrder - b.sortOrder).forEach(cabinet => {
    entries.push({
      type: cabinet.directFormType ? 'directForm' : 'link',
      key: `${cabinet.identifier}Tab`,
      className: `t-h-nav-${cabinet.identifier}`,
      href: cabinet.directFormType ? undefined : `/#${cabinet.path}`,
      icon: cabinet.icon,
      label: cabinet.title,
      identifier: cabinet.identifier,
      directFormType: cabinet.directFormType
    });
  });
  Object.entries(groupedCabinets).filter(([tabGroupIdentifier, groupCabinetList]) => tabGroupIdentifier !== 'admin' && (groupCabinetList === null || groupCabinetList === void 0 ? void 0 : groupCabinetList.length)).sort((a, b) => {
    const orderDiff = getTabGroupSortOrder(a[0], _tabGroups) - getTabGroupSortOrder(b[0], _tabGroups);
    if (orderDiff !== 0) return orderDiff;
    return a[0].localeCompare(b[0]);
  }).forEach(([tabGroupIdentifier, groupCabinetList]) => {
    const groupInfo = _tabGroups.find(group => group.identifier === tabGroupIdentifier);
    const sortedCabinets = [...groupCabinetList].sort((a, b) => a.sortOrder - b.sortOrder);
    entries.push({
      type: 'dropdown',
      key: `${tabGroupIdentifier}Dropdown`,
      className: `t-h-nav-${tabGroupIdentifier}-dropdown`,
      icon: (groupInfo === null || groupInfo === void 0 ? void 0 : groupInfo.icon) || 'folder',
      label: (groupInfo === null || groupInfo === void 0 ? void 0 : groupInfo.title) || tabGroupIdentifier,
      tabGroupIdentifier,
      items: sortedCabinets.map(cabinet => ({
        type: cabinet.directFormType ? 'directForm' : 'link',
        key: cabinet.identifier,
        className: `t-h-nav-${cabinet.identifier}`,
        href: cabinet.directFormType ? undefined : `/#${cabinet.path}`,
        icon: cabinet.icon,
        label: cabinet.title,
        identifier: cabinet.identifier,
        directFormType: cabinet.directFormType
      }))
    });
  });
  return entries;
};

/**
 * Sidebar nav entries — flat list or hierarchical (tab group dropdowns) matching top layout rules.
 */
export const getSidebarNavEntries = ({
  user,
  cabinets: _cabinets4 = [],
  dashboards: _dashboards4 = [],
  tabGroups: _tabGroups2 = []
}) => {
  if (!user) return [];
  if (shouldUseHierarchicalNav(_cabinets4)) {
    return getHierarchicalNavEntries({
      user,
      cabinets: _cabinets4,
      dashboards: _dashboards4,
      tabGroups: _tabGroups2
    });
  }
  return getNavItems({
    user,
    cabinets: _cabinets4,
    dashboards: _dashboards4
  }).map(item => _objectSpread({
    type: 'link'
  }, item));
};
export default getNavItems;